/* eslint-disable canonical/filename-match-exported */
import ServiceQuery, {
  type ServicePageQuery,
} from '@/__generated__/relay/ServicePageQuery.graphql.js';
import { MissingResource } from '@/components/MissingResource/MissingResource.js';
import { PageHeadFragment } from '@/components/PageHead/PageHead.js';
import { TemplateRenderer } from '@/components/TemplateRenderer/TemplateRenderer.js';
import SERVICE_QUERY from '@/documents/queries/ServicePage.js';
import { getPreloadedQuery } from '@/services/relay/network.js';
import { isPlaywrightTestEnvironment } from '@/utilities/isPlaywrightTestEnvironment.js';
import { normalizeUsername } from '@/utilities/normalizeUsername.js';
import { redirectInvalidUsername } from '@/utilities/redirectInvalidUsername.js';
import { resolveGraphQLResponse } from '@/utilities/resolveGraphQLResponse.js';
import { resolveMetadaUrl } from '@/utilities/resolveMetadataUrl.js';
import { resolveUsername } from '@/utilities/resolveUsername.js';
import { type GetServerSidePropsContext } from 'next';
import dynamic from 'next/dynamic';
import { type PreloadedQuery, usePreloadedQuery } from 'react-relay';

const PageProviders = dynamic(
  async () => await import('@/components/PageProviders/PageProviders.js'),
  {
    ssr: false,
  },
);

const Service = ({
  isCustomDomain,
  isPlaywright,
  queryRefs,
  ...props
}: {
  readonly isCustomDomain: boolean;
  readonly isPlaywright: boolean;
  readonly queryRefs: { query: PreloadedQuery<ServicePageQuery> };
}) => {
  const queryRef = usePreloadedQuery<ServicePageQuery>(
    SERVICE_QUERY,
    queryRefs.query,
  );

  if (!queryRef?.productizedServiceBySlug || !queryRef?.userProfileByUsername)
    return <MissingResource />;

  const analyticsResourceData = {
    globalId: queryRef?.productizedServiceBySlug.id,
    id: queryRef?.productizedServiceBySlug.analyticsId,
    type: 'service' as const,
  };

  const { productizedServiceBySlug } = queryRef;
  const templateProps = { ...props, queryRef: productizedServiceBySlug };

  return (
    <PageProviders
      analyticsResourceData={analyticsResourceData}
      isPlaywright={isPlaywright}
      templateQueryRef={queryRef}
      userQueryRef={queryRef.userProfileByUsername}
      {...props}
    >
      <PageHeadFragment
        isCustomDomain={isCustomDomain}
        queryNode={queryRef}
      />
      <TemplateRenderer
        page="Service"
        {...templateProps}
      />
    </PageProviders>
  );
};

export const getServerSideProps = async (
  context: GetServerSidePropsContext,
) => {
  const isPlaywright = isPlaywrightTestEnvironment(context);
  const { isCustomDomain, username } = await resolveUsername(context);
  if (!username) {
    return redirectInvalidUsername(context);
  }

  const pageUrl = resolveMetadaUrl(context, username);
  const pageQuery = await getPreloadedQuery(ServiceQuery, {
    slug: context.params?.['slug'],
    url: pageUrl,
    username,
  });

  const queryData = resolveGraphQLResponse(pageQuery.response);
  if (!queryData.data?.['productizedServiceBySlug']) {
    return {
      notFound: true,
    };
  }

  const queryUsername =
    queryData.data?.['productizedServiceBySlug']?.userProfile?.displayUsername;
  const normalizedUsername = normalizeUsername(queryUsername);

  if (normalizedUsername !== username) {
    // If this service does not belong to the user, return a 404
    return {
      notFound: true,
    };
  }

  return {
    props: {
      isCustomDomain,
      isPlaywright,
      preloadedQueries: {
        query: pageQuery,
      },
      username,
    },
  };
};

export default Service;
